export const fmtReportAmount = (n) =>
  Number(n || 0).toLocaleString("en-PK", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

export const fmtReportRs = (n) => `Rs ${fmtReportAmount(n)}`;

export const buildReportSummary = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {
      total_sales: 0,
      customer_sales: 0,
      guest_sales: 0,
      total_expenses: 0,
      net_after_expenses: 0,
      cash_received_from_customers: 0,
      cash_paid_to_companies: 0,
      total_cash_in: 0,
      net_cash_movement: 0,
      invoice_count: 0,
      guest_invoice_count: 0,
      expense_count: 0,
      total_customer_returns: 0,
      total_guest_returns: 0,
      total_returns: 0,
      customer_return_count: 0,
      guest_return_count: 0,
      total_cash_expenses: 0,
      total_cash_loans_given: 0,
      cash_loan_count: 0,
      total_cash_loan_repayments: 0,
      cash_loan_repayment_count: 0,
      customer_cash_payment_count: 0,
      company_cash_payment_count: 0,
      cash_adjustment_count: 0,
      cash_adjustment_total: 0,
      cash_transferred_out: 0,
      cash_transferred_in: 0,
      opening_cash: 0,
      closing_cash: 0,
      cash_remaining_after_transfers: 0,
    };
  }

  if (raw.summary && typeof raw.summary === "object") {
    return raw.summary;
  }

  const transactions = raw.transactions || [];
  const guests = raw.guest_transactions || [];
  const expenses = (raw.expenses || []).filter((e) => e.isDeleted !== true);
  const payments = raw.payments || [];

  const sumInv = (rows) =>
    rows.reduce((s, r) => s + Number(r.total_amount || 0), 0);
  const guestSales = sumInv(guests);
  const customerSales = sumInv(transactions);
  const totalSales = guestSales + customerSales;
  const totalExpenses = expenses.reduce(
    (s, e) => s + Number(e.expense || 0),
    0
  );
  const totalCashExpenses = expenses
    .filter((e) => {
      const accountName = String(
        e.accountId?.account_name || e.account_name || ""
      )
        .trim()
        .toUpperCase();
      return accountName === "CASH" || Number(e.accountId?.type) === 1;
    })
    .reduce((s, e) => s + Number(e.expense || 0), 0);

  const customerReturns = (raw.returns || []).filter(
    (row) => row.isDeleted !== true
  );
  const guestReturns = (raw.guest_returns || []).filter(
    (row) => row.isDeleted !== true
  );
  const totalCustomerReturns = customerReturns.reduce(
    (s, row) => s + Number(row.total_amount || 0),
    0
  );
  const totalGuestReturns = guestReturns.reduce(
    (s, row) => s + Number(row.total_amount || 0),
    0
  );
  const totalReturns = totalCustomerReturns + totalGuestReturns;

  const cashLoans = (raw.cash_loans || []).filter((row) => row.isDeleted !== true);
  const cashLoanRepayments = (raw.cash_loan_repayments || []).filter(
    (row) => row.isDeleted !== true
  );
  const totalCashLoansGiven = cashLoans.reduce(
    (s, row) => s + Number(row.amount || 0),
    0
  );
  const totalCashLoanRepayments = cashLoanRepayments.reduce(
    (s, row) => s + Number(row.amount || 0),
    0
  );

  const activePayments = payments.filter((p) => p.isDeleted !== true);
  const cashReceived = activePayments
    .filter((p) => p.user_type === "Customer" && Number(p.payment_type) === 1)
    .reduce((s, p) => s + Number(p.amount || 0), 0);
  const cashPaidCompanies = activePayments
    .filter((p) => p.user_type === "Company" && Number(p.payment_type) === 1)
    .reduce((s, p) => s + Number(p.amount || 0), 0);
  const totalCashIn = cashReceived + guestSales;
  const netCashMovement =
    totalCashIn -
    cashPaidCompanies -
    totalCashExpenses -
    totalCashLoansGiven +
    totalCashLoanRepayments;

  const activeAdjustments = (raw.cash_adjustments || []).filter(
    (row) => row.isDeleted !== true
  );
  const isCashLabel = (name) =>
    String(name || "").trim().toUpperCase() === "CASH";
  let cashTransferredOut = 0;
  let cashTransferredIn = 0;
  activeAdjustments.forEach((row) => {
    const amount = Number(row.amount || 0);
    const fromName = row.from_account || row.fromAccountId?.account_name;
    const toName = row.to_account || row.toAccountId?.account_name;
    if (isCashLabel(fromName)) cashTransferredOut += amount;
    if (isCashLabel(toName)) cashTransferredIn += amount;
  });

  return {
    total_sales: totalSales,
    customer_sales: customerSales,
    guest_sales: guestSales,
    total_expenses: totalExpenses,
    total_cash_expenses: totalCashExpenses,
    total_cash_loans_given: totalCashLoansGiven,
    cash_loan_count: cashLoans.length,
    total_cash_loan_repayments: totalCashLoanRepayments,
    cash_loan_repayment_count: cashLoanRepayments.length,
    total_customer_returns: totalCustomerReturns,
    total_guest_returns: totalGuestReturns,
    total_returns: totalReturns,
    customer_return_count: customerReturns.length,
    guest_return_count: guestReturns.length,
    net_after_expenses: totalSales - totalExpenses,
    cash_received_from_customers: cashReceived,
    cash_paid_to_companies: cashPaidCompanies,
    total_cash_in: totalCashIn,
    net_cash_movement: netCashMovement,
    invoice_count: transactions.length + guests.length,
    guest_invoice_count: guests.length,
    expense_count: expenses.length,
    customer_cash_payment_count: activePayments.filter(
      (p) => p.user_type === "Customer" && Number(p.payment_type) === 1
    ).length,
    company_cash_payment_count: activePayments.filter(
      (p) => p.user_type === "Company" && Number(p.payment_type) === 1
    ).length,
    cash_adjustment_count: (raw.cash_adjustments || []).filter(
      (row) => row.isDeleted !== true
    ).length,
    cash_adjustment_total: activeAdjustments.reduce(
      (s, row) => s + Number(row.amount || 0),
      0
    ),
    cash_transferred_out: cashTransferredOut,
    cash_transferred_in: cashTransferredIn,
    opening_cash: Number(raw.opening_cash ?? raw.summary?.opening_cash ?? 0),
    closing_cash: Number(
      raw.closing_cash ??
        raw.summary?.closing_cash ??
        netCashMovement - cashTransferredOut + cashTransferredIn
    ),
    cash_remaining_after_transfers: Number(
      raw.cash_remaining_after_transfers ??
        raw.summary?.cash_remaining_after_transfers ??
        raw.closing_cash ??
        raw.summary?.closing_cash ??
        netCashMovement - cashTransferredOut + cashTransferredIn
    ),
  };
};
